import { useState } from 'react';
import { motion } from 'framer-motion';
import type { Mission } from '../../types';
import { MISSION_TYPE, feelingById } from '../../constants/bridge';
import Glyph from '../glyphs/Glyph';

interface Props {
  mission: Mission;
  /** Therapist's first name, used as the signature. */
  signature: string;
  index: number;
  feelingId?: string;
  onComplete: () => void;
}

/**
 * A mission as a handwritten note pinned to the fridge: the therapist's "why"
 * in their own hand, the task underneath. Finishing it folds the note into a
 * paper plane that flies back across the bridge.
 */
export default function NoteLetter({ mission, signature, index, feelingId, onComplete }: Props) {
  const [folding, setFolding] = useState(false);
  const type = MISSION_TYPE[mission.type] ?? { glyph: 'star', label: mission.type };
  const tilt = [-1.6, 1.2, -0.6, 1.8][index % 4];

  if (mission.is_completed) {
    const f = feelingById(feelingId);
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex items-center gap-3 rounded-2xl bg-card/70 border border-dashed border-border px-4 py-3"
      >
        <span className="w-9 h-9 rounded-full bg-bridge-soft text-bridge-foreground flex items-center justify-center shrink-0">
          <Glyph name="plane" size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-foreground truncate">{mission.title}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            עף בחזרה ל{signature}
            {f && (
              <>
                {' · '}
                <Glyph name={f.glyph} size={14} />
                {f.label}
              </>
            )}
          </p>
        </div>
        <span className="text-xs font-bold text-muted-foreground flex items-center gap-0.5 shrink-0">
          +{mission.xp_reward}
          <Glyph name="diamond" size={13} />
        </span>
      </motion.div>
    );
  }

  const fold = () => {
    if (folding) return;
    setFolding(true);
    window.setTimeout(onComplete, 650);
  };

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 30, rotate: tilt * 2 }}
      animate={
        folding
          ? { opacity: 0, y: -160, x: -40, rotate: -28, scale: 0.25 }
          : { opacity: 1, y: 0, rotate: tilt, scale: 1 }
      }
      transition={folding ? { duration: 0.65, ease: [0.5, 0, 0.75, 0] } : { type: 'spring', stiffness: 160, damping: 18, delay: 0.15 + index * 0.08 }}
      whileHover={folding ? undefined : { rotate: 0, y: -3 }}
      className="relative paper-ruled rounded-[6px] shadow-[0_10px_24px_-12px_rgba(47,38,30,0.45),0_2px_0_rgba(47,38,30,0.06)] px-5 pt-7 pb-4"
    >
      {/* washi tape */}
      <span
        aria-hidden="true"
        className="absolute -top-3 left-1/2 -translate-x-1/2 w-20 h-6 rotate-[-3deg] rounded-[3px] bg-bridge/60"
      />
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-bold text-muted-foreground flex items-center gap-1">
          <Glyph name={type.glyph} size={16} />
          {type.label}
        </span>
        <span className="text-[11px] font-bold text-muted-foreground flex items-center gap-0.5">
          +{mission.xp_reward}
          <Glyph name="diamond" size={13} />
        </span>
      </div>

      {mission.rationale && (
        <p className="font-hand text-[28px] leading-[28px] text-primary mt-1">
          {mission.rationale}
        </p>
      )}

      <h3 className="font-extrabold text-foreground text-[17px] leading-snug mt-2">{mission.title}</h3>
      {mission.description && (
        <p className="text-[13px] text-muted-foreground leading-[28px]">{mission.description}</p>
      )}

      <div className="flex items-end justify-between gap-3 mt-2">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={fold}
          disabled={folding}
          className="bg-primary text-primary-foreground font-bold text-sm rounded-full ps-4 pe-5 py-2.5 flex items-center gap-2 hover:bg-primary/90 shadow-[0_6px_14px_-6px_hsl(var(--primary))]"
        >
          <span className="bg-primary-foreground rounded-full p-0.5 flex"><Glyph name="plane" size={18} /></span>
          עשיתי, לשלוח בחזרה
        </motion.button>
        <span className="font-hand text-2xl text-primary/80 leading-none" aria-label={`מאת ${signature}`}>
          {signature}
        </span>
      </div>
    </motion.article>
  );
}
